import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Spacing12 } from 'autogen/design-tokens/tokens';
import type { ImageContent, VideoContent } from 'autogen/swagger/all';
import { SeeLess, SeeMore } from 'autogen/translation-keys/trans-website-shared-buttons';
import AspectRatioContainer from 'components/generic/AspectRatioContainer/AspectRatioContainer';
import InlineButton from 'components/generic/InlineButton';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import VideoPlayer from 'components/video/VideoPlayer';
import useIsOverflowing from 'hooks/globals/useIsOverflowing';
import { breakpoint, sizes } from 'variables';

type CustomMediaContentProps = {
    customMediaContent: Array<ImageContent | VideoContent>;
};

const Wrapper = styled.div`
    margin: 0 auto;
    width: 100%;
    scroll-margin-top: 150px;

    ${breakpoint.up(sizes.sm)} {
        width: 414px;
    }

    ${breakpoint.up(sizes.md)} {
        width: 100%;
    }

    ${breakpoint.up(sizes.lg)} {
        width: 860px;
        scroll-margin-top: 25px;
    }
`;

const Content = styled.div<{ collapsed: boolean }>`
    ${({ collapsed }) =>
        collapsed &&
        css`
            overflow: hidden;
            max-height: 800px;

            ${breakpoint.up(sizes.md)} {
                max-height: 1150px;
            }
        `}
`;

const StyledPicture = styled(PictureDeprecated)`
    width: 100%;

    img {
        width: 100%;
    }
`;

const PlayerAspectRatioContainer = styled(AspectRatioContainer)`
    height: 100%;
`;

const ReadMoreToggle = styled.div<{ collapsed: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: end;
    align-items: start;
    width: 100%;
    height: ${({ collapsed }) => (collapsed ? '75px' : 'auto')};
    background: linear-gradient(0deg, #ffffff 72.16%, rgba(255, 255, 255, 0) 100%);
    margin-top: ${({ collapsed }) => (collapsed ? '-75px' : `${Spacing12}`)};
`;

export default function CustomMediaContent(props: CustomMediaContentProps) {
    const { customMediaContent } = props;
    const [collapsed, setCollapsed] = useState(true);
    const contentRef = useRef<HTMLImageElement>(null);
    const isOverflowing = useIsOverflowing(contentRef, true);
    const { t } = useTranslation();

    function getVideoUrl(item) {
        switch (item.platform) {
            case 'Vimeo':
                item.url = `https://player.vimeo.com/video/${item.id}`;
                break;
            case 'YouTube':
                item.url = `https://www.youtube.com/embed/${item.id}?autoplay=0&wmode=transparent&controls=2&showinfo=0&playsinline=1&enablejsapi=1&rel=0`;
                break;
            default:
                break;
        }

        return item;
    }

    function getItem(i, item) {
        switch (item.$type) {
            case 'ImageContent':
                return <StyledPicture key={i} {...item.responsiveImage} />;
            case 'VideoContent':
                getVideoUrl(item);

                return (
                    <PlayerAspectRatioContainer key={i} ratioHeight={16} ratioWidth={9}>
                        <VideoPlayer playing={false} video={item} />
                    </PlayerAspectRatioContainer>
                );
            default:
                break;
        }

        return undefined;
    }

    return (
        <Wrapper id="customMediaContentSection">
            {isOverflowing ? (
                <>
                    <Content collapsed={collapsed}>{customMediaContent.map((item, i) => getItem(i, item))}</Content>
                    <ReadMoreToggle collapsed={collapsed}>
                        <InlineButton variant="dark" fontWeight="semibold" onClick={() => setCollapsed(!collapsed)}>
                            {collapsed ? t(SeeMore) : t(SeeLess)}
                        </InlineButton>
                    </ReadMoreToggle>
                </>
            ) : (
                <Content collapsed ref={contentRef}>
                    {customMediaContent.map((item, i) => getItem(i, item))}
                </Content>
            )}
        </Wrapper>
    );
}
